import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Phone, Globe, Star, MessageCircle, ChevronDown, ChevronUp, Copy, Check, SlidersHorizontal } from 'lucide-react';
import { toast } from 'react-toastify';

const STATUS_CONFIG = {
  new:            { label: 'Yeni',               color: 'bg-blue-100 text-blue-700 border-blue-200' },
  contacted:      { label: 'İletişime Geçildi',  color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  not_interested: { label: 'İlgilenmiyor',       color: 'bg-red-100 text-red-700 border-red-200' },
  converted:      { label: 'Dönüştü ✓',          color: 'bg-green-100 text-green-700 border-green-200' },
};

const PRICE_LEVEL = ['Bilinmiyor', '₺', '₺₺', '₺₺₺', '₺₺₺₺'];

const TABS = [
  { key: 'all',            label: 'Tümü' },
  { key: 'new',            label: 'Yeni' },
  { key: 'contacted',      label: 'İletişime Geçildi' },
  { key: 'not_interested', label: 'İlgilenmiyor' },
  { key: 'converted',      label: 'Dönüştü' },
];

const renderAIAnalysis = (text) => {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    if (line.startsWith('### ')) {
      return (
        <div key={i} className="font-bold text-gray-800 mt-4 mb-1.5 text-sm border-l-2 border-blue-400 pl-2">
          {line.replace('### ', '')}
        </div>
      );
    }
    if (line.startsWith('## ')) {
      return (
        <div key={i} className="font-extrabold text-gray-900 mt-5 mb-2 text-sm">
          {line.replace('## ', '')}
        </div>
      );
    }
    const parts = line.split(/\*\*(.*?)\*\*/g);
    return (
      <p key={i} className="text-sm text-gray-600 leading-relaxed">
        {parts.map((part, j) =>
          j % 2 === 1 ? <strong key={j} className="text-gray-800">{part}</strong> : part
        )}
      </p>
    );
  });
};

const StarRating = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`h-3.5 w-3.5 ${i <= Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200 fill-gray-200'}`}
      />
    ))}
    <span className="text-xs text-gray-500 ml-1 font-medium">{rating?.toFixed(1)}</span>
  </div>
);

const LeadCard = ({ lead, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(lead.notes || '');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleStatusChange = async (newStatus) => {
    setSaving(true);
    try {
      const updates = { status: newStatus };
      if (newStatus === 'contacted' && !lead.contactedAt) {
        updates.contactedAt = serverTimestamp();
      }
      await updateDoc(doc(db, 'marketing_leads', lead.id), updates);
      onUpdate(lead.id, updates);
      toast.success('Durum güncellendi');
    } catch (e) {
      toast.error('Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'marketing_leads', lead.id), { notes });
      onUpdate(lead.id, { notes });
      toast.success('Not kaydedildi');
    } catch (e) {
      toast.error('Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const copyAnalysis = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(lead.ai_analysis || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const whatsappPhone = lead.phone?.replace(/\D/g, '');
  const hasPhone = lead.phone && lead.phone !== 'Not found';
  const hasWebsite = lead.website && lead.website !== 'Not found';
  const statusCfg = STATUS_CONFIG[lead.status || 'new'];

  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm flex flex-col gap-0 overflow-hidden">
      {/* Card Header */}
      <div className="p-5 flex flex-col gap-3">
        {/* Name + Status */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-gray-900 truncate">{lead.name}</h3>
            <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mt-1.5">
              <StarRating rating={lead.rating} />
              <span className="text-gray-300 text-xs">|</span>
              <span className="text-xs text-gray-500">{lead.total_reviews} yorum</span>
              <span className="text-gray-300 text-xs">|</span>
              <span className="text-xs text-gray-500 font-medium">
                {PRICE_LEVEL[lead.price_level] || PRICE_LEVEL[0]}
              </span>
            </div>
          </div>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border flex-shrink-0 ${statusCfg.color}`}>
            {statusCfg.label}
          </span>
        </div>

        {/* Contact Buttons */}
        <div className="flex flex-wrap gap-2">
          {whatsappPhone && (
            <a
              href={`https://wa.me/90${whatsappPhone}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold rounded-lg transition-colors"
            >
              <MessageCircle className="h-3.5 w-3.5" />
              WhatsApp
            </a>
          )}
          {hasPhone && (
            <a
              href={`tel:${lead.phone}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold rounded-lg transition-colors"
            >
              <Phone className="h-3.5 w-3.5" />
              {lead.phone}
            </a>
          )}
          {hasWebsite && (
            <a
              href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-600 text-xs font-semibold rounded-lg transition-colors"
            >
              <Globe className="h-3.5 w-3.5" />
              Website
            </a>
          )}
        </div>

        {/* Status Dropdown */}
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-400 whitespace-nowrap w-12">Durum</label>
          <select
            value={lead.status || 'new'}
            onChange={(e) => handleStatusChange(e.target.value)}
            disabled={saving}
            className="flex-1 text-xs px-2.5 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:opacity-50"
          >
            <option value="new">Yeni</option>
            <option value="contacted">İletişime Geçildi</option>
            <option value="not_interested">İlgilenmiyor</option>
            <option value="converted">Dönüştü</option>
          </select>
        </div>

        {/* Notes */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs text-gray-400">Not</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Superadmin notu ekle..."
            className="text-xs px-2.5 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
          {notes !== (lead.notes || '') && (
            <button
              onClick={handleSaveNotes}
              disabled={saving}
              className="self-end text-xs px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          )}
        </div>
      </div>

      {/* AI Analysis — Collapsible */}
      {lead.ai_analysis && (
        <div className="border-t border-gray-100">
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
          >
            <span className="text-xs font-semibold text-gray-600">🤖 AI Satış Analizi</span>
            <div className="flex items-center gap-2">
              {expanded && (
                <button
                  onClick={copyAnalysis}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                  title="Kopyala"
                >
                  {copied
                    ? <Check className="h-3.5 w-3.5 text-green-600" />
                    : <Copy className="h-3.5 w-3.5 text-gray-400" />}
                </button>
              )}
              {expanded
                ? <ChevronUp className="h-4 w-4 text-gray-400" />
                : <ChevronDown className="h-4 w-4 text-gray-400" />}
            </div>
          </button>
          {expanded && (
            <div className="px-5 py-4 max-h-96 overflow-y-auto bg-white">
              {renderAIAnalysis(lead.ai_analysis)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [sortBy, setSortBy] = useState('rating');

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const snap = await getDocs(collection(db, 'marketing_leads'));
        setLeads(snap.docs.map(d => ({ id: d.id, status: 'new', ...d.data() })));
      } catch (e) {
        console.error('Error fetching leads:', e);
        toast.error('Leadler yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    fetchLeads();
  }, []);

  const handleUpdate = (id, updates) => {
    setLeads(prev => prev.map(l => l.id === id ? { ...l, ...updates } : l));
  };

  const filtered = leads
    .filter(l => activeTab === 'all' || (l.status || 'new') === activeTab)
    .sort((a, b) => {
      if (sortBy === 'rating')  return (b.rating || 0) - (a.rating || 0);
      if (sortBy === 'reviews') return (b.total_reviews || 0) - (a.total_reviews || 0);
      return 0;
    });

  const countBy = (key) =>
    key === 'all' ? leads.length : leads.filter(l => (l.status || 'new') === key).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Potansiyel Müşteriler</h1>
          <p className="text-sm text-gray-500 mt-1">
            n8n Google Places + AI analizi — {leads.length} işletme bulundu
          </p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {TABS.slice(1).map(tab => {
            const count = countBy(tab.key);
            const cfg = STATUS_CONFIG[tab.key];
            return (
              <div key={tab.key} className={`rounded-xl border px-4 py-3 ${cfg.color}`}>
                <div className="text-2xl font-extrabold">{count}</div>
                <div className="text-xs mt-0.5 opacity-80">{tab.label}</div>
              </div>
            );
          })}
        </div>

        {/* Tabs + Sort */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 ${
                  activeTab === tab.key
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-300 hover:text-blue-600'
                }`}
              >
                {tab.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                  activeTab === tab.key ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'
                }`}>
                  {countBy(tab.key)}
                </span>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="rating">Puana Göre ↓</option>
              <option value="reviews">Yorum Sayısına Göre ↓</option>
            </select>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-20 text-gray-400 text-sm">Yükleniyor...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-400 text-sm">Bu filtrede lead bulunamadı.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filtered.map(lead => (
              <LeadCard key={lead.id} lead={lead} onUpdate={handleUpdate} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLeads;
